import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoFiltersCard from "../../../components/eco-ui/EcoFiltersCard";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";
import { downloadBlob } from "../../dp/utils/format";
import { HOMEPAGE } from "../../home/routes";
import {
  exportAiUsageApi,
  fmtBrlFromUsd,
  fmtUsd,
  getAiUsageSummaryApi,
  listAiUsageApi,
  type AiUsageFilters,
  type AiUsageRow,
  type AiUsageSummary,
} from "../api/aiUsageApi";
import { ADMIN_CONFIGURACOES_PAGE, ADMIN_SISTEMA_PAGE } from "../routes";
import "./AdminSistema.css";
import "../../dp/pages/DpEco.css";
import "../../campanhas/pages/Campanhas.css";
import "./AiUsage.css";

type Preset = "hoje" | "7d" | "mes" | "mesPassado" | "custom";

const PRESETS: { value: Preset; label: string }[] = [
  { value: "hoje", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "mes", label: "Este mês" },
  { value: "mesPassado", label: "Mês passado" },
  { value: "custom", label: "Escolher" },
];

/** Dia de São Paulo, `YYYY-MM-DD`. */
const spDay = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

function periodOf(preset: Preset): { from: string; to: string } | null {
  const today = spDay(new Date());
  const [y, m] = today.split("-").map(Number);
  switch (preset) {
    case "hoje":
      return { from: today, to: today };
    case "7d":
      return { from: spDay(new Date(Date.now() - 6 * 86_400_000)), to: today };
    case "mes":
      return { from: `${today.slice(0, 8)}01`, to: today };
    case "mesPassado": {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
      const mm = String(pm).padStart(2, "0");
      return { from: `${py}-${mm}-01`, to: `${py}-${mm}-${last}` };
    }
    default:
      return null;
  }
}

const fmtDateTime = (iso: string) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
const fmtInt = (n: number) => n.toLocaleString("pt-BR");
const fmtDay = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/** Gasto por dia: uma série, barras com ponta arredondada e dica ao passar o mouse. */
const SpendChart: React.FC<{ days: AiUsageSummary["byDay"]; rate: number | null }> = ({ days, rate }) => {
  const [active, setActive] = useState<number | null>(null);
  const W = 720;
  const H = 180;
  const P = { top: 10, right: 8, bottom: 24, left: 52 };
  const max = Math.max(0.01, ...days.map((d) => d.costUsd));
  const innerW = W - P.left - P.right;
  const innerH = H - P.top - P.bottom;
  const slot = innerW / Math.max(1, days.length);
  const barW = Math.max(2, Math.min(26, slot - 3));
  const y = (v: number) => P.top + innerH - (v / max) * innerH;
  const every = Math.ceil(days.length / 8);
  const a = active != null ? days[active] : null;
  return (
    <div className="aiuChart" onMouseLeave={() => setActive(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Gasto com IA por dia">
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line className="aiuChart__grid" x1={P.left} x2={W - P.right} y1={y(max * f)} y2={y(max * f)} />
            <text className="aiuChart__axis" x={P.left - 6} y={y(max * f) + 4} textAnchor="end">
              {fmtUsd(max * f)}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          const x = P.left + slot * i + (slot - barW) / 2;
          const h = Math.max(0, y(0) - y(d.costUsd));
          const r = Math.min(4, barW / 2, h);
          return (
            <g key={d.date}>
              <rect x={P.left + slot * i} y={P.top} width={slot} height={innerH} fill="transparent" onMouseEnter={() => setActive(i)} onClick={() => setActive(i)} />
              {h > 0 ? (
                <path
                  className={`aiuChart__bar${active === i ? " is-active" : ""}`}
                  pointerEvents="none"
                  d={`M${x},${y(0)} V${y(0) - h + r} Q${x},${y(0) - h} ${x + r},${y(0) - h} H${x + barW - r} Q${x + barW},${y(0) - h} ${x + barW},${y(0) - h + r} V${y(0)} Z`}
                />
              ) : null}
              {i % every === 0 ? (
                <text className="aiuChart__axis" x={P.left + slot * i + slot / 2} y={H - 6} textAnchor="middle">
                  {fmtDay(d.date)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {a && active != null ? (
        <div className="aiuChart__tip" style={{ left: `${((P.left + slot * active + slot / 2) / W) * 100}%`, top: `${(y(a.costUsd) / H) * 100}%` }}>
          {fmtDay(a.date)}: {fmtUsd(a.costUsd)}
          {rate ? ` (${fmtBrlFromUsd(a.costUsd, rate)})` : ""} · {a.calls} chamada(s)
        </div>
      ) : null}
    </div>
  );
};

/**
 * Administração → Sistema → Consumo de IA (31/10/2026): quem usou IA, para quê,
 * quando e quanto custou, com filtros por período, usuário, função e resultado.
 * Cada chamada é registrada pelo backend, inclusive as que falharam.
 */
export default function AiUsagePage() {
  const navigate = useNavigate();
  const goBack = useGoBack(ADMIN_SISTEMA_PAGE());
  const [preset, setPreset] = useState<Preset>("mes");
  const [filters, setFilters] = useState<AiUsageFilters>(() => ({ ...periodOf("mes")! }));
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [rows, setRows] = useState<{ total: number; page: number; pageSize: number; items: AiUsageRow[] } | null>(null);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const key = JSON.stringify(filters);

  const load = useCallback(async () => {
    const f: AiUsageFilters = JSON.parse(key);
    try {
      const [s, l] = await Promise.all([getAiUsageSummaryApi(f), listAiUsageApi(f, page)]);
      setSummary(s);
      setRows(l);
    } catch (e: any) {
      toast.error(e?.message);
    }
  }, [key, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [key]);

  const choosePreset = (p: Preset) => {
    setPreset(p);
    const period = periodOf(p);
    if (period) setFilters((prev) => ({ ...prev, ...period }));
  };

  const set = <K extends keyof AiUsageFilters>(k: K, v: AiUsageFilters[K]) => setFilters((prev) => ({ ...prev, [k]: v || undefined }));

  const activeCount = (filters.userId ? 1 : 0) + (filters.feature ? 1 : 0) + (filters.provider ? 1 : 0) + (filters.success ? 1 : 0);
  const rate = summary?.dollarRate ?? null;
  const pct = summary ? Math.min(100, Math.round((summary.month.spentUsd / Math.max(0.01, summary.month.limitUsd)) * 100)) : 0;
  const avg = summary && summary.totals.calls ? summary.totals.costUsd / summary.totals.calls : 0;

  const exportCsv = async () => {
    try {
      setExporting(true);
      downloadBlob(await exportAiUsageApi(filters), `consumo-ia-${filters.from}-a-${filters.to}.csv`);
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setExporting(false);
    }
  };

  const summaryText = useMemo(() => {
    const parts = [`${fmtDay(filters.from)} a ${fmtDay(filters.to)}`];
    if (filters.userId) parts.push(summary?.options.users.find((u) => u.id === filters.userId)?.name ?? (filters.userId === "SISTEMA" ? "Sistema" : "1 usuário"));
    if (filters.feature) parts.push(summary?.options.features.find((f) => f.feature === filters.feature)?.label ?? filters.feature);
    if (filters.provider) parts.push(summary?.options.providers.find((p) => p.id === filters.provider)?.label ?? filters.provider);
    return parts.join(" · ");
  }, [filters, summary]);

  return (
    <div className="dpePage">
      <main className="dpePage__main aiuMain">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Administração", onClick: () => navigate(ADMIN_SISTEMA_PAGE()) },
            { label: "Consumo de IA" },
          ]}
          title="Consumo de IA"
          subtitle="Quem usou inteligência artificial no sistema, para quê, quando e quanto custou. A Anthropic cobra em dólar, por quantidade de texto lido e escrito (tokens)."
          onBack={goBack}
          backLabel="Voltar para Administração"
          actions={
            <button type="button" className="ecoBtn" onClick={exportCsv} disabled={exporting || !summary?.totals.calls}>
              <EcoIcon name="download" size={18} />
              {exporting ? "Exportando..." : "Exportar CSV"}
            </button>
          }
        />

        {summary ? (
          <section className="ecoCard aiuMonth" aria-label="Gasto do mês">
            <div className="aiuMonth__text">
              <strong>
                Este mês: {fmtUsd(summary.month.spentUsd)} de {fmtUsd(summary.month.limitUsd, 0)}
              </strong>
              <span className="cmpHint">
                {summary.month.reached
                  ? "Limite atingido: as funções de IA estão paradas até o mês que vem."
                  : `${pct}% do limite mensal. Ao atingir, as funções de IA param até o mês seguinte.`}{" "}
                <button type="button" className="ecoLinkBtn" onClick={() => navigate(ADMIN_CONFIGURACOES_PAGE())}>
                  Mudar o limite
                </button>
              </span>
            </div>
            <div className="aiuBar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Gasto do mês sobre o limite">
              <span className={`aiuBar__fill${pct >= 90 ? " is-high" : ""}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="aiuPills">
              <span className={`ecoPill${summary.enabled ? "" : " ecoPill--off"}`}>{summary.enabled ? `Textos: Anthropic · ${summary.model}` : "Textos: IA desligada (sem chave)"}</span>
              <span className={`ecoPill${summary.imageEnabled ? "" : " ecoPill--off"}`}>
                {summary.imageEnabled ? `Imagens: Google · ${summary.imageModel}` : "Imagens: gerador desligado"}
              </span>
            </span>
          </section>
        ) : null}

        <EcoFiltersCard summary={summaryText} activeCount={activeCount}>
          <EcoSegmented label="Período" options={PRESETS} value={preset} onChange={choosePreset} />
          <div className="ecoFilters__grid">
            <div className="ecoField">
              <label htmlFor="aiu-de">De</label>
              <input
                id="aiu-de"
                type="date"
                className="ecoInput ecoInput--sm"
                value={filters.from}
                max={filters.to}
                onChange={(e) => {
                  setPreset("custom");
                  if (e.target.value) set("from", e.target.value);
                }}
              />
            </div>
            <div className="ecoField">
              <label htmlFor="aiu-ate">Até</label>
              <input
                id="aiu-ate"
                type="date"
                className="ecoInput ecoInput--sm"
                value={filters.to}
                min={filters.from}
                onChange={(e) => {
                  setPreset("custom");
                  if (e.target.value) set("to", e.target.value);
                }}
              />
            </div>
            <div className="ecoField">
              <label htmlFor="aiu-user">Usuário</label>
              <select id="aiu-user" className="ecoSelect ecoSelect--sm" value={filters.userId ?? ""} onChange={(e) => set("userId", e.target.value)}>
                <option value="">Todos</option>
                {(summary?.options.users ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
                <option value="SISTEMA">Sistema (rotinas)</option>
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="aiu-feature">Para quê</label>
              <select id="aiu-feature" className="ecoSelect ecoSelect--sm" value={filters.feature ?? ""} onChange={(e) => set("feature", e.target.value)}>
                <option value="">Todas as funções</option>
                {(summary?.options.features ?? []).map((f) => (
                  <option key={f.feature} value={f.feature}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="aiu-provider">Provedor</label>
              <select id="aiu-provider" className="ecoSelect ecoSelect--sm" value={filters.provider ?? ""} onChange={(e) => set("provider", e.target.value as AiUsageFilters["provider"])}>
                <option value="">Todos</option>
                {(summary?.options.providers ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="aiu-result">Resultado</label>
              <select id="aiu-result" className="ecoSelect ecoSelect--sm" value={filters.success ?? ""} onChange={(e) => set("success", e.target.value as AiUsageFilters["success"])}>
                <option value="">Todos</option>
                <option value="true">Sucesso</option>
                <option value="false">Falha</option>
              </select>
            </div>
          </div>
          <div className="ecoFilters__row">
            <button
              type="button"
              className="ecoBtn ecoFilters__clear"
              onClick={() => {
                setPreset("mes");
                setFilters({ ...periodOf("mes")! });
              }}
            >
              Limpar filtros
            </button>
          </div>
        </EcoFiltersCard>

        {!summary ? (
          <p className="cmpLoading">Carregando...</p>
        ) : (
          <>
            <div className="aiuKpis">
              <div className="ecoCard aiuKpi">
                <span className="aiuKpi__label">Gasto no período</span>
                <span className="aiuKpi__value">{fmtUsd(summary.totals.costUsd)}</span>
                {rate ? <span className="aiuKpi__sub">≈ {fmtBrlFromUsd(summary.totals.costUsd, rate)}</span> : null}
              </div>
              <div className="ecoCard aiuKpi">
                <span className="aiuKpi__label">Chamadas</span>
                <span className="aiuKpi__value">{fmtInt(summary.totals.calls)}</span>
                <span className="aiuKpi__sub">{summary.totals.failures ? `${fmtInt(summary.totals.failures)} com falha` : "nenhuma falha"}</span>
              </div>
              <div className="ecoCard aiuKpi">
                <span className="aiuKpi__label">Custo médio por chamada</span>
                <span className="aiuKpi__value">{fmtUsd(avg, 3)}</span>
                {rate ? <span className="aiuKpi__sub">≈ {fmtBrlFromUsd(avg, rate)}</span> : null}
              </div>
              <div className="ecoCard aiuKpi">
                <span className="aiuKpi__label">Texto processado (tokens)</span>
                <span className="aiuKpi__value">{fmtInt(summary.totals.inputTokens + summary.totals.outputTokens)}</span>
                <span className="aiuKpi__sub">
                  {fmtInt(summary.totals.inputTokens)} lidos · {fmtInt(summary.totals.outputTokens)} escritos
                </span>
              </div>
            </div>

            <section className="ecoCard aiuPanel">
              <h2 className="aiuPanel__title">Gasto por dia</h2>
              {summary.totals.calls ? <SpendChart days={summary.byDay} rate={rate} /> : <p className="ecoEmpty">Nenhum uso de IA neste período.</p>}
            </section>

            <div className="aiuSplit">
              <section className="ecoCard aiuPanel">
                <h2 className="aiuPanel__title">Por usuário</h2>
                {summary.byUser.length ? (
                  <table className="ecoTable">
                    <thead>
                      <tr>
                        <th>Quem</th>
                        <th className="ecoTable__num">Chamadas</th>
                        <th className="ecoTable__num">Gasto</th>
                        <th className="ecoTable__num">Do total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.byUser.map((u) => (
                        <tr key={u.userId} className="aiuRow" onClick={() => set("userId", u.userId)} title="Filtrar por este usuário">
                          <td>{u.name}</td>
                          <td className="ecoTable__num">{fmtInt(u.calls)}</td>
                          <td className="ecoTable__num">{fmtUsd(u.costUsd)}</td>
                          <td className="ecoTable__num">{summary.totals.costUsd ? `${Math.round((u.costUsd / summary.totals.costUsd) * 100)}%` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="ecoEmpty">Sem uso no período.</p>
                )}
              </section>
              <section className="ecoCard aiuPanel">
                <h2 className="aiuPanel__title">Por função</h2>
                {summary.byFeature.length ? (
                  <table className="ecoTable">
                    <thead>
                      <tr>
                        <th>Para quê</th>
                        <th className="ecoTable__num">Chamadas</th>
                        <th className="ecoTable__num">Gasto</th>
                        <th className="ecoTable__num">Do total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.byFeature.map((f) => (
                        <tr key={f.feature} className="aiuRow" onClick={() => set("feature", f.feature)} title="Filtrar por esta função">
                          <td>{f.label}</td>
                          <td className="ecoTable__num">{fmtInt(f.calls)}</td>
                          <td className="ecoTable__num">{fmtUsd(f.costUsd)}</td>
                          <td className="ecoTable__num">{summary.totals.costUsd ? `${Math.round((f.costUsd / summary.totals.costUsd) * 100)}%` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="ecoEmpty">Sem uso no período.</p>
                )}
              </section>
            </div>

            <section className="ecoCard aiuPanel">
              <h2 className="aiuPanel__title">Por provedor e modelo</h2>
              {summary.byModel.length ? (
                <table className="ecoTable">
                  <thead>
                    <tr>
                      <th>Provedor · modelo</th>
                      <th className="ecoTable__num">Chamadas</th>
                      <th className="ecoTable__num">Gasto</th>
                      <th className="ecoTable__num">Do total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.byModel.map((m) => (
                      <tr key={`${m.provider}|${m.model}`} className="aiuRow" onClick={() => set("provider", m.provider)} title="Filtrar por este provedor">
                        <td>{m.label}</td>
                        <td className="ecoTable__num">{fmtInt(m.calls)}</td>
                        <td className="ecoTable__num">{fmtUsd(m.costUsd)}</td>
                        <td className="ecoTable__num">{summary.totals.costUsd ? `${Math.round((m.costUsd / summary.totals.costUsd) * 100)}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="ecoEmpty">Sem uso no período.</p>
              )}
              {summary.totals.images ? <p className="cmpSub">{fmtInt(summary.totals.images)} imagens geradas no período.</p> : null}
            </section>

            <section className="ecoCard aiuPanel">
              <h2 className="aiuPanel__title">Cada chamada</h2>
              {!rows?.items.length ? (
                <p className="ecoEmpty">Nenhuma chamada com esses filtros.</p>
              ) : (
                <>
                  <div className="ecoTableWrap">
                    <table className="ecoTable">
                      <thead>
                        <tr>
                          <th>Quando</th>
                          <th>Quem</th>
                          <th>Para quê</th>
                          <th>Provedor · modelo</th>
                          <th className="ecoTable__num">Tokens (lidos / escritos)</th>
                          <th className="ecoTable__num">Custo</th>
                          <th className="ecoTable__num">Tempo</th>
                          <th>Resultado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.items.map((r) => (
                          <tr key={r.id}>
                            <td className="ecoTable__muted">{fmtDateTime(r.occurredAt)}</td>
                            <td>{r.userName}</td>
                            <td>
                              {r.featureLabel}
                              {r.summary ? <span className="cmpSub">{r.summary}</span> : null}
                            </td>
                            <td className="ecoTable__muted">
                              {r.modelLabel}
                              {r.imageCount ? <span className="cmpSub">{r.imageCount === 1 ? "1 imagem" : `${r.imageCount} imagens`}</span> : null}
                            </td>
                            <td className="ecoTable__num">
                              {fmtInt(r.inputTokens + r.cacheReadTokens + r.cacheWriteTokens)} / {fmtInt(r.outputTokens)}
                            </td>
                            <td className="ecoTable__num">
                              {fmtUsd(r.costUsd, 4)}
                              {rate ? <span className="cmpSub">{fmtBrlFromUsd(r.costUsd, rate)}</span> : null}
                            </td>
                            <td className="ecoTable__num">{(r.durationMs / 1000).toFixed(1)} s</td>
                            <td>
                              {r.success ? (
                                <span className="ecoPill">Sucesso</span>
                              ) : (
                                <span className="ecoPill cmpPill--bad" title={r.errorMessage ?? ""}>
                                  Falha
                                </span>
                              )}
                              {!r.success && r.errorMessage ? <span className="cmpSub">{r.errorMessage}</span> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <EcoPagination page={rows.page} pageSize={rows.pageSize} total={rows.total} onChange={setPage} />
                </>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
